// 导入React框架及状态管理工具
import React, { useState, useEffect } from 'react'
// 导入国际化钩子和i18n实例
import { useTranslation } from '@/i18n'
import i18n from '@/i18n/config'

// 导入图标库Lucide的图标组件（可从https://lucide.dev/icons/查看更多图标）
import { Home, Film, List, Wrench, Settings, User, LogOut, Workflow } from 'lucide-react'

// 导入Logo图标
import logoIcon from '@/assets/icons/vs_64.png'

// 导入路由相关工具函数，用于页面导航和路径获取
import { useLocation, useNavigate } from 'react-router-dom'

// 导入导航配置数据，包含所有导航菜单和子菜单的结构与信息
import { navigationConfig, accountMenu } from '@/utils/navigation'

// 导入工具函数，用于合并CSS类名
import { cn } from '@/utils'
import { logoutApi } from '@/utils/api'
import axios from '@/utils/api'

// API 基础配置
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL

// 获取请求头
const getHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || ''
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }
}

// 通用请求处理
const handleResponse = async <T,>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
  }

  const data = await response.json()

  // 根据API文档格式处理响应
  if (data.code !== 200) {
    throw new Error(data.message || 'API request failed')
  }

  return data.data as T
}

// 侧边栏组件的属性定义
// 定义组件接收的外部参数类型
interface SidebarProps {
  collapsed: boolean;        // 是否折叠的状态 (true=折叠, false=展开)
  toggleSidebar: () => void; // 从父组件传入的函数，用于切换侧边栏折叠/展开状态
}

// 侧边栏组件的核心实现
// React.FC 表示这是一个函数式组件，接收 SidebarProps 类型的参数
const Sidebar: React.FC<SidebarProps> = ({ collapsed, toggleSidebar }) => {
  // 路由相关钩子，用于获取当前页面的路径信息
  const location = useLocation()

  // 路由导航钩子，用于编程式跳转到其他页面
  const navigate = useNavigate()

  // 国际化钩子
  const { t } = useTranslation()

  // 用于管理哪些父菜单是展开的状态
  // 使用 Set 数据结构存储展开的菜单 ID，默认展开 'production', 'history', 'tools'
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(
    new Set(['production', 'history', 'tools'])
  )

  // 用于控制账户下拉菜单的显示/隐藏状态
  const [showAccountDropdown, setShowAccountDropdown] = useState(false)

  // 用户信息状态
  const [nickname, setNickname] = useState<string>('')
  // 会员状态
  const [membershipStatus, setMembershipStatus] = useState<any>(null)

  // 从localStorage获取用户信息
  useEffect(() => {
    const fetchUserInfo = () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const user = JSON.parse(userJson);
          if (user.nickname) {
            setNickname(user.nickname);
          }
        } catch (error) {
          console.error('Error parsing user info:', error);
        }
      }
    };

    // 初始加载时获取用户信息
    fetchUserInfo();

    // 监听localStorage变化
    const handleStorageChange = () => {
      fetchUserInfo();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 获取会员状态
  useEffect(() => {
    const fetchMembershipStatus = async () => {
      try {
        // 调用订阅接口获取会员状态
        const response = await fetch(`${API_BASE_URL}/subscriptions`, {
          method: 'GET',
          headers: getHeaders()
        })

        const data = await handleResponse<any>(response)
        setMembershipStatus(data)
      } catch (error) {
        console.error('获取会员状态失败:', error)
        setMembershipStatus(null)
      }
    }

    // 只有登录用户才获取会员状态
    if (localStorage.getItem('token')) {
      fetchMembershipStatus()
    }
  }, []);

  // 格式化日期
  const formatDate = (dateString: string) => {
    if (!dateString) return '无'

    const date = new Date(dateString)

    // 检查日期是否有效，如果是无效日期（如0001-01-01），返回"无"
    if (isNaN(date.getTime()) || date.toISOString().startsWith('0001-01-01')) {
      return '无'
    }

    return date.toISOString().split('T')[0]
  }

  // 获取会员类型名称
  const getSubscriptionTypeName = (type: number) => {
    if (!type) return t('membership.subscriptionType.none')

    const typeMap: Record<number, 'none' | 'basic' | 'pro' | 'business' | 'unknown'> = {
      1: 'basic',
      2: 'pro',
      3: 'business'
    }

    const typeKey = typeMap[type] || 'unknown'
    return t(`membership.subscriptionType.${typeKey}`)
  }

  // ====================== 辅助函数 ======================
  // 根据图标名称获取对应的图标组件
  // 参数:
  // - iconName: 图标名称字符串 (与导航配置中的 icon 字段对应)
  // - noMargin: 布尔值，是否去掉图标右边距 (用于折叠状态下的图标居中)
  const getIconComponent = (iconName: string, noMargin = false) => {
    // 设置图标样式类名
    // - 展开状态下大小为 w-4 h-4 (16px x 16px)，右边距为 mr-2 (8px)
    // - 折叠状态下大小为 w-8 h-8 (32px x 32px)，居中显示
    const className = noMargin ? "w-6 h-6 opacity-80" : "w-4 h-4 mr-2 opacity-80"

    // 根据图标名称返回对应的 Lucide React 图标组件
    // 使用 switch 语句匹配不同图标名称
    switch (iconName) {
      // 主页图标
      case 'Home':
        return <Home className={className} />
      // 视频图标
      case 'Film':
        return <Film className={className} />
      // 列表图标
      case 'List':
        return <List className={className} />
      // 工具图标
      case 'Tools':
        return <Wrench className={className} />
      // 设置图标
      case 'Settings':
        return <Settings className={className} />
      // 用户图标
      case 'User':
        return <User className={className} />
      // 登出图标
      case 'LogOut':
        return <LogOut className={className} />
      // 工作流图标
      case 'Workflow':
        return <Workflow className={className} />
      default:
        return null // 如果没有匹配到图标，则返回 null (避免显示错误)
    }
  }

  // 切换菜单的展开/折叠状态
  // 参数: menuId - 菜单的唯一标识符
  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => {
      // 创建当前展开菜单集合的副本，避免直接修改原状态
      const newSet = new Set(prev)

      // 切换菜单状态: 如果已展开则折叠，已折叠则展开
      if (newSet.has(menuId)) {
        newSet.delete(menuId) // 移除ID表示折叠菜单
      } else {
        newSet.add(menuId)    // 添加ID表示展开菜单
      }

      return newSet // 返回新的状态集合
    })
  }

  // 判断某个导航项是否处于活跃状态
  // 参数: path - 导航项指向的页面路径
  // 返回: 布尔值，如果当前页面路径与导航项路径一致则返回true
  const isActive = (path: string) => {
    // 比较当前页面的路径 (location.pathname) 和导航项的路径
    return location.pathname === path
  }

  // 处理导航点击事件
  // 参数: path - 要导航到的页面路径
  const handleNavigation = (path: string) => {
    if (path === '/logout') {
      // 处理登出逻辑
      logoutApi()
        .then(() => {
          // 清除本地存储
          localStorage.removeItem('token')
          localStorage.removeItem('user')

          // 清除axios默认的Authorization头
          axios.defaults.headers.common['Authorization'] = ''

          // 更新用户状态
          setNickname('')

          // 导航到登录页面
          navigate('/login', {
            state: { message: 'Logout successful. Please login again.' }
          })
        })
        .catch((error) => {
          console.error('Logout error:', error)

          // 即使API调用失败，也清除本地存储并导航到登录页面
          localStorage.removeItem('token')
          localStorage.removeItem('user')

          // 清除axios默认的Authorization头
          axios.defaults.headers.common['Authorization'] = ''

          setNickname('')
          navigate('/login')
        })
    } else {
      // 正常导航逻辑
      navigate(path)
    }

    setShowAccountDropdown(false) // 导航后自动关闭账户下拉菜单，提升用户体验
    if (collapsed) toggleSidebar() // 折叠状态下点击菜单自动展开侧边栏
  }

  // 点击空白处关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const accountDropdown = document.querySelector('.account-dropdown');
      const accountButton = document.querySelector('.account-button');

      // 检查点击目标是否在下拉菜单或账户按钮之外
      if (
        accountDropdown &&
        accountButton &&
        !accountDropdown.contains(event.target as Node) &&
        !accountButton.contains(event.target as Node)
      ) {
        setShowAccountDropdown(false);
      }
    };

    // 只有当下拉菜单显示时才添加事件监听器
    if (showAccountDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // 清理事件监听器
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountDropdown]);

  return (
    <div className={cn(
      // 基础样式：渐变背景色从深灰到更深灰，毛玻璃效果，右侧边框，flex垂直布局，平滑过渡
      // - bg-gradient-to-b from-gray-950/98 via-gray-950/95 to-gray-900/90: 渐变背景色
      // - backdrop-blur-lg: 毛玻璃效果
      // - border-r border-gray-800/80: 右侧边框
      // - flex-col: flex垂直布局
      // - transition-all duration-300 ease-in-out: 所有样式平滑过渡
      'bg-gradient-to-b from-gray-950/98 via-gray-950/95 to-gray-900/90 backdrop-blur-lg border-r border-gray-800/80 flex flex-col h-full transition-all duration-300 ease-in-out',

      // 动态宽度：折叠时w-14(56px)，展开时w-[188px]（比原宽度减少20px）
      collapsed ? 'w-14' : 'w-[188px]'
    )}>
      {/* 侧边栏折叠/展开切换按钮 */}
      <div
        className="absolute right-[-10px] top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-300 z-50"
        onClick={toggleSidebar}
      >
        <svg
          className="w-3 h-3 text-gray-400 hover:text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {collapsed ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          )}
        </svg>
      </div>

      {/* Logo 区域 */}
      {/* 侧边栏顶部的品牌标识区域 */}
      <div className="p-0 min-h-[64px] flex items-center">
        {/* 根据折叠状态显示不同的 Logo 样式 */}
        {!collapsed ? (
          // 展开状态：显示完整 Logo 和品牌名称
          <div className="flex items-center">
            <img src={logoIcon} alt="V" className="w-8 h-8 ml-3 mr-1.5 self-center" />
            {/* 品牌名称：VisSynth */}
            <h1 className="text-lg font-bold self-center mt-0.5">VisSynth</h1>
          </div>
        ) : (
          // 折叠状态：仅显示 Logo 图标，居中显示
          <div className="flex justify-center w-full">
            <img src={logoIcon} alt="V" className="w-8 h-8" />
          </div>
        )}
      </div>

      {/* 导航菜单区域 */}
      {/* 侧边栏的核心内容，包含所有导航项 */}
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-4 ${!collapsed ? '' : 'flex flex-col items-center'}`}>
        {/* 根据折叠状态渲染不同的导航菜单样式 */}
        {!collapsed ? (
          // 展开状态：显示图标和文字
          navigationConfig.map((item) => (
            // 遍历导航配置数据，渲染每个导航项
            <div key={item.id} className="">
              {/* 判断导航项是否有子菜单 */}
              {item.children ? (
                // 有子菜单的情况：渲染可折叠的父菜单
                <>
                  {/* 父菜单头部，点击可展开/折叠子菜单 */}
                  <div
                    className={cn(
                      // 基础样式：
                      // - collapsible-header：可折叠头部的基础类
                      // - flex items-center justify-between：图标文字在左，箭头在右
                      // - px-3 py-3：内边距，与普通菜单项保持一致
                      // - mx-2 rounded-lg：外边距和圆角
                      // - cursor-pointer：鼠标悬停时显示指针
                      // - transition-all：平滑过渡效果
                      // - text-gray-500：默认文字颜色
                      // - hover:text-white：鼠标悬停时文字变白
                      'collapsible-header flex items-center justify-between px-3 py-3 mx-2 rounded-lg cursor-pointer transition-all duration-300 text-gray-400 hover:text-white',

                      // 展开状态下的样式：
                      // - active：添加激活类，箭头会旋转
                      expandedMenus.has(item.id) && 'active'
                    )}
                    onClick={() => toggleMenu(item.id)} // 点击时切换子菜单展开/折叠状态
                  >
                    {/* 父菜单左侧：图标 + 文字 */}
                    <div className="flex items-center">
                      {getIconComponent(item.icon)} {/* 获取并渲染图标 */}
                      <span className="font-medium sidebar-text">{t(item.label)}</span> {/* 菜单文字 */}
                    </div>

                    {/* 父菜单右侧：箭头图标（通过 CSS 实现，见 index.css） */}
                    <div className="chevron"></div>
                  </div>

                  {/* 子菜单列表，仅在父菜单展开时显示 */}
                  {expandedMenus.has(item.id) && (
                    <div className="mt-1">
                      {/* 遍历子菜单数据，渲染每个子菜单项 */}
                      {item.children.map((child) => (
                        <div
                          key={child.id}
                          className={cn(
                            'submenu-item', // 子菜单的基础样式类
                            isActive(child.path) && 'active' // 如果是当前页面，则添加激活样式
                          )}
                          onClick={() => handleNavigation(child.path)} // 点击时导航到子菜单对应的页面
                        >
                          <span>{t(child.label)}</span> {/* 子菜单文字 */}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // 没有子菜单的情况：直接渲染导航项
                <div
                  className={cn(
                    'sidebar-item mx-2', // 导航项的基础样式类，设置与父菜单相同的左右边距
                    isActive(item.path) && 'active' // 如果是当前页面，则添加激活样式
                  )}
                  onClick={() => handleNavigation(item.path)} // 点击时导航到对应的页面
                >
                  <div className="flex items-center">
                    {getIconComponent(item.icon)} {/* 图标 */}
                    <span className="font-medium">{t(item.label)}</span> {/* 文字 */}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          // 折叠状态：仅显示图标，居中排列
          navigationConfig.map((item) => (
            <div
              key={item.id}
              className={cn(
                // 基础样式：
                // - flex items-center justify-center：图标居中
                // - px-2 py-3：内边距
                // - my-1：垂直外边距
                // - w-8 h-8：固定宽高，确保图标大小一致
                // - mx-auto：水平居中
                // - rounded-lg：圆角
                // - cursor-pointer：鼠标指针
                // - transition-all：平滑过渡
                // - text-gray-500：默认文字颜色
                // - hover:text-white：悬停时文字变白
                // - hover:bg-white/10：悬停时背景色变浅
                'flex items-center justify-center my-1 w-8 h-8 mx-auto rounded-lg cursor-pointer transition-all duration-300 text-gray-500 hover:text-white hover:bg-white/10',

                // 激活状态样式：
                // - bg-white/10：背景色变浅
                // - text-white：文字变白
                isActive(item.path) && 'bg-white/10 text-white'
              )}
              // 点击时导航：
              // - 如果有子菜单，则导航到第一个子菜单的路径
              // - 如果没有子菜单，则直接导航到当前项的路径
              onClick={() => handleNavigation(item.children ? item.children[0].path : item.path)}
            >
              {getIconComponent(item.icon, true)} {/* 获取并渲染图标，noMargin=true 确保图标居中 */}
            </div>
          ))
        )}
      </nav>

      {/* 侧边栏底部区域 */}
      {/* 仅在展开状态下显示，包含会员信息/升级会员横幅和 "账户信息" */}
      {!collapsed && (
        <div className="shrink-0 bg-gradient-to-b from-transparent to-gray-900/95">
          {/* 会员状态或升级会员横幅 - 位于账户信息上方 */}
          <div className="pt-0 pb-0 px-3 w-full">
            {membershipStatus?.active_subscription ? (
              // 会员状态显示
              <div
                className="bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-gray-700/50 rounded-xl py-3 px-6 shadow-lg transition-all duration-300 hover:shadow-xl w-full text-center cursor-pointer"
                onClick={() => navigate('/membership')}
              >
                <div className={`font-bold ${membershipStatus.active_subscription.subscription_type === 3 || membershipStatus.active_subscription.subscription_type === 2 ? 'text-lg' : 'text-sm'} ${membershipStatus.active_subscription.subscription_type === 3 ? 'bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-600 bg-clip-text text-transparent' : ''}`}>
                  {/* 处理中英文不同的空格需求：中文不加空格 */}
                  {`${getSubscriptionTypeName(membershipStatus.active_subscription.subscription_type)} ${t('membership.member')}`.replace(' 会员', '会员')}
                </div>
                <div className="text-xs opacity-90">
                  {formatDate(membershipStatus.active_subscription.end_date)}
                </div>
              </div>
            ) : (
              // 非会员，显示升级会员横幅
              <div
                className="bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-gray-700/50 rounded-xl py-3 px-6 shadow-lg transition-all duration-300 hover:shadow-xl w-full text-center cursor-pointer"
                onClick={() => navigate('/membership')}
              >
                <div className="text-sm font-medium ">升级会员</div> {/* 横幅标题 */}
                <div className="text-xs opacity-90">解锁全部功能</div> {/* 横幅说明 */}
              </div>
            )}
          </div>

          {/* 账户信息区域 */}
          <div className="p-3">
            {/* 账户信息与下拉菜单 */}
            <div className="relative">
              {/* 账户信息按钮 - 点击可展开/折叠下拉菜单 */}
              <div
                className={cn(
                  'sidebar-item', // 基础样式类
                  'account-button', // 添加账户按钮类名，用于事件监听
                  'px-2 flex justify-center', // 左右padding设置为8px，并使内容居中
                  showAccountDropdown && 'bg-white/10' // 展开状态下的背景色
                )}
                onClick={() => setShowAccountDropdown(!showAccountDropdown)} // 点击时切换下拉菜单状态
              >
                <div className="flex items-center justify-center">
                  {getIconComponent('User')} {/* 用户图标 */}
                  <span className="font-medium text-sm truncate overflow-hidden whitespace-nowrap max-w-[100px]">{nickname || '未登录'}</span> {/* 显示nickname或默认文字 */}
                </div>
              </div>

              {/* 账户下拉菜单 - 仅在展开状态下显示 */}
              {showAccountDropdown && (
                <div className="account-dropdown absolute bottom-full left-2 right-2 mb-2 bg-gray-800/95  border border-gray-700 rounded-lg py-2 shadow-xl z-10">
                  {/* 遍历账户菜单配置数据，渲染下拉菜单项 */}
                  {accountMenu.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        // 基础样式：
                        // - flex items-center：内容居中
                        // - px-4 py-2：内边距
                        // - mx-2 rounded：外边距和圆角
                        // - text-gray-400：默认文字颜色（与其他菜单一致）
                        // - hover:text-white：鼠标悬停时文字变白
                        // - hover:bg-white/5：鼠标悬停时背景色变浅
                        // - cursor-pointer：鼠标指针
                        // - transition-colors：颜色过渡效果
                        'flex items-center px-4 py-2 mx-2 rounded text-gray-400 hover:text-white hover:bg-white/5 cursor-pointer transition-colors',

                        // 激活状态样式：
                        // - text-white：文字变为白色（与其他菜单一致）
                        isActive(item.path) && 'text-white'
                      )}
                      onClick={() => handleNavigation(item.path)} // 点击时导航到对应的页面
                    >
                      <span className="text-sm">{t(item.label)}</span> {/* 下拉菜单项文字 */}
                    </div>
                  ))}

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sidebar